import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';

declare global {
    interface Window {
        Chargebee?: {
            init: (config: { site: string; publishableKey: string }) => any;
        };
    }
}

@Injectable({ providedIn: 'root' })
export class ChargebeeService {
    private loadPromise: Promise<void> | null = null;
    private instancePromise: Promise<any> | null = null;

    constructor(@Inject(ENVIRONMENT) private environment: Environment) {}

    /**
     * Ensures Chargebee.js v2 is loaded.
     */
    public load(): Promise<void> {
        if (typeof window !== 'undefined' && window.Chargebee?.init) {
            return Promise.resolve();
        }

        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = new Promise<void>((resolve, reject) => {
            if (typeof document === 'undefined') {
                resolve();
                return;
            }

            const existing = document.querySelector<HTMLScriptElement>('script[data-chargebee-js="v2"]');
            if (existing) {
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', () => reject(new Error('Failed to load Chargebee.js')));
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://js.chargebee.com/v2/chargebee.js';
            script.async = true;
            script.defer = true;
            script.dataset.chargebeeJs = 'v2';
            script.addEventListener('load', () => resolve());
            script.addEventListener('error', () => reject(new Error('Failed to load Chargebee.js')));
            document.head.appendChild(script);
        });

        return this.loadPromise;
    }

    /**
     * Returns a singleton Chargebee instance initialized with the current environment.
     */
    public async getInstance(): Promise<any> {
        if (this.instancePromise) {
            return this.instancePromise;
        }

        this.instancePromise = this.load().then(() => {
            if (!window.Chargebee?.init) {
                throw new Error('Chargebee.js not available on window');
            }

            return window.Chargebee.init({
                site: this.environment.chargebeeConfig.site,
                publishableKey: this.environment.chargebeeConfig.publishableKey,
            });
        });

        return this.instancePromise;
    }
}

